import { useState, useEffect } from "react";
import { Radar } from "lucide-react";
import { ReactionButtons } from "../ReactionButtons";

export const KeyFindingBanner = ({ careApps, carePercent, insights }) => {
  const [currentInsightIndex, setCurrentInsightIndex] = useState(0);
  const [isInsightVisible, setIsInsightVisible] = useState(true);

  useEffect(() => {
    if (insights.length === 0) return;
    const interval = setInterval(() => {
      setIsInsightVisible(false);
      setTimeout(() => {
        setCurrentInsightIndex((prev) => (prev + 1) % insights.length);
        setIsInsightVisible(true);
      }, 500);
    }, 8000);
    return () => clearInterval(interval);
  }, [insights.length]);

  const animation = isInsightVisible
    ? "slideInRight 0.5s ease-out forwards"
    : "slideOutLeft 0.4s ease-in forwards";

  const jumpTo = (idx) => {
    setIsInsightVisible(false);
    setTimeout(() => {
      setCurrentInsightIndex(idx);
      setIsInsightVisible(true);
    }, 400);
  };

  return (
    <div className="bg-gradient-to-r from-emerald-500/10 via-teal-500/10 to-cyan-500/10 border border-emerald-500/30 rounded-xl p-4 md:p-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex-1 min-w-0">
          <p className="text-xs md:text-sm text-muted-foreground mb-1">Observing AI for Humanity</p>
          <p className="text-lg md:text-2xl font-bold leading-snug">
            <span className="text-emerald-400">{careApps}</span> AI apps built for human wellbeing
            <span className="text-xs font-normal text-cyan-400 ml-1">({carePercent}%)</span>
          </p>
          <p className="text-xs text-muted-foreground mt-1.5 leading-relaxed hidden sm:block">
            Care-centered AI prioritizes health, education, accessibility, and community.
          </p>
        </div>
      </div>

      {insights.length > 0 && (
        <div className="mt-4 pt-4 border-t border-emerald-500/20">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-full bg-emerald-500/20 flex-shrink-0">
              <Radar className="w-4 h-4 text-emerald-400" />
            </div>
            <div
              className="relative overflow-hidden min-h-[40px] flex-1"
              data-testid="rotating-insight"
            >
              <div
                key={currentInsightIndex}
                className="animate-slide-in-insight"
                style={{ animation }}
              >
                <p className="text-sm font-medium text-white/90">{insights[currentInsightIndex]}</p>
                <div className="flex items-center gap-3 mt-1.5">
                  <p className="text-xs text-white/50">
                    Humanity Insight • {currentInsightIndex + 1}/{insights.length}
                  </p>
                  <ReactionButtons insightId={`insight-${currentInsightIndex}`} />
                </div>
              </div>
            </div>
          </div>
          <div className="flex gap-1.5 mt-3 justify-center">
            {insights.map((_, idx) => {
              const active = idx === currentInsightIndex;
              return (
                <button
                  key={`dot-${idx}`}
                  onClick={() => jumpTo(idx)}
                  className={`h-1.5 rounded-full transition-all duration-500 cursor-pointer ${
                    active ? "bg-emerald-400 w-5" : "bg-emerald-400/30 w-1.5 hover:bg-emerald-400/50"
                  }`}
                />
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default KeyFindingBanner;
